import AsyncStorage from '@react-native-async-storage/async-storage';

const TOKEN_KEY = 'auth_token';
const THEME_KEY = 'theme_mode';
const LANG_KEY = 'display_language';
const REGISTERED_PUSH_TOKEN_KEY = 'registered_expo_push_token';
const PENDING_PUSH_TOKEN_DELETION_KEY = 'pending_push_token_deletion';
const notificationPromptKey = (userId: number) => `notification_prompt_dismissed:${userId}`;
const contactTranslationKey = (ownerId: number, contactId: number) =>
  `contact_translation:${ownerId}:${contactId}`;
const calendarConnectionEnabledKey = (userId: number) => `calendar_connection_enabled:${userId}`;
const selectedCalendarIdsKey = (userId: number) => `selected_calendar_ids:${userId}`;
const calendarViewModeKey = (userId: number) => `calendar_view_mode:${userId}`;
// 9.32 / D-655 — link görünümü tercihi; hesap ve cihaz kapsamında.
const linkViewDefaultKey = (userId: number) => `link_view_default:${userId}`;
const linkViewAskKey = (userId: number) => `link_view_ask:${userId}`;
// D-666 — konuşmanın duygusu (Plutchik); yalnız bu cihazda, hesap ve kişi başına.
// İçerik değil, tek bir duygu adı saklanır.
const avatarMoodKey = (userId: number, otherId: number) => `avatar_mood:${userId}:${otherId}`;
const hiddenExternalCalendarEventsKey = (userId: number) =>
  `hidden_external_calendar_events:${userId}`;
const systemCalendarEventKey = (userId: number, meetupId: number) =>
  `system_calendar_event:${userId}:${meetupId}`;

export type RegisteredPushToken = {
  token: string;
  provider: 'expo' | 'fcm';
  deletionKey?: string;
};

export type SystemCalendarEventMapping = {
  eventId: string;
  calendarId: string;
  state?: 'linked' | 'externally-removed' | 'externally-changed';
  observedStartDate?: string;
  observedEndDate?: string;
  scheduleRevision?: number;
};

async function readPushRegistration(key: string): Promise<RegisteredPushToken | null> {
  const raw = await AsyncStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<RegisteredPushToken>;
    if (typeof parsed.token === 'string' && (parsed.provider === 'expo' || parsed.provider === 'fcm')) {
      return {
        token: parsed.token,
        provider: parsed.provider,
        ...(typeof parsed.deletionKey === 'string' ? { deletionKey: parsed.deletionKey } : {}),
      };
    }
  } catch {
    // D-123'teki kısa ömürlü eski sürüm yalnız token dizgesi saklıyordu.
    if (key === REGISTERED_PUSH_TOKEN_KEY) return { token: raw, provider: 'expo' };
  }
  return null;
}

const getRegisteredPushToken = () => readPushRegistration(REGISTERED_PUSH_TOKEN_KEY);
const getPendingPushTokenDeletion = () => readPushRegistration(PENDING_PUSH_TOKEN_DELETION_KEY);

// Aynı ayara peş peşe dokunulduğunda eski, yavaş yazımın yeni seçimin üstüne
// gelmesini engelle. Anahtar başına yazımlar çağrı sırasıyla tamamlanır.
const writeQueues = new Map<string, Promise<void>>();
function enqueueWrite(key: string, operation: () => Promise<void>): Promise<void> {
  const previous = writeQueues.get(key) ?? Promise.resolve();
  const next = previous.catch(() => {}).then(operation);
  writeQueues.set(key, next);
  return next.finally(() => {
    if (writeQueues.get(key) === next) writeQueues.delete(key);
  });
}

export const storage = {
  getToken: () => AsyncStorage.getItem(TOKEN_KEY),
  setToken: (token: string) => enqueueWrite(TOKEN_KEY, () => AsyncStorage.setItem(TOKEN_KEY, token)),
  clearToken: () => enqueueWrite(TOKEN_KEY, () => AsyncStorage.removeItem(TOKEN_KEY)),
  getThemeMode: () => AsyncStorage.getItem(THEME_KEY),
  setThemeMode: (mode: string) => enqueueWrite(THEME_KEY, () => AsyncStorage.setItem(THEME_KEY, mode)),
  getDisplayLanguage: () => AsyncStorage.getItem(LANG_KEY),
  setDisplayLanguage: (lang: string) => enqueueWrite(LANG_KEY, () => AsyncStorage.setItem(LANG_KEY, lang)),
  getRegisteredPushToken,
  setRegisteredPushToken: (registration: RegisteredPushToken) =>
    enqueueWrite(
      REGISTERED_PUSH_TOKEN_KEY,
      () => AsyncStorage.setItem(REGISTERED_PUSH_TOKEN_KEY, JSON.stringify(registration))
    ),
  clearRegisteredPushToken: () =>
    enqueueWrite(REGISTERED_PUSH_TOKEN_KEY, () => AsyncStorage.removeItem(REGISTERED_PUSH_TOKEN_KEY)),
  getPendingPushTokenDeletion,
  setPendingPushTokenDeletion: (registration: RegisteredPushToken) =>
    enqueueWrite(
      PENDING_PUSH_TOKEN_DELETION_KEY,
      () => AsyncStorage.setItem(PENDING_PUSH_TOKEN_DELETION_KEY, JSON.stringify(registration))
    ),
  clearPendingPushTokenDeletion: () =>
    enqueueWrite(
      PENDING_PUSH_TOKEN_DELETION_KEY,
      () => AsyncStorage.removeItem(PENDING_PUSH_TOKEN_DELETION_KEY)
    ),
  getNotificationPromptDismissed: async (userId: number) =>
    (await AsyncStorage.getItem(notificationPromptKey(userId))) === '1',
  dismissNotificationPrompt: (userId: number) =>
    enqueueWrite(
      notificationPromptKey(userId),
      () => AsyncStorage.setItem(notificationPromptKey(userId), '1')
    ),
  getContactTranslation: async (ownerId: number, contactId: number) =>
    (await AsyncStorage.getItem(contactTranslationKey(ownerId, contactId))) === '1',
  setContactTranslation: (ownerId: number, contactId: number, enabled: boolean) =>
    enqueueWrite(
      contactTranslationKey(ownerId, contactId),
      () => AsyncStorage.setItem(contactTranslationKey(ownerId, contactId), enabled ? '1' : '0')
    ),
  getCalendarConnectionEnabled: async (userId: number): Promise<boolean> => {
    const value = await AsyncStorage.getItem(calendarConnectionEnabledKey(userId));
    if (value === '1') return true;
    if (value === '0') return false;
    // Eski sürümde ayrı bağlantı anahtarı yoktu. Daha önce açıkça seçilmiş bir
    // takvim varsa bağlantıyı koru; yalnız sistem izninden bağlantı varsayma.
    return (await storage.getSelectedCalendarIds(userId)).length > 0;
  },
  setCalendarConnectionEnabled: (userId: number, enabled: boolean) =>
    enqueueWrite(
      calendarConnectionEnabledKey(userId),
      () => AsyncStorage.setItem(calendarConnectionEnabledKey(userId), enabled ? '1' : '0')
    ),
  getSelectedCalendarIds: async (userId: number): Promise<string[]> => {
    const raw = await AsyncStorage.getItem(selectedCalendarIdsKey(userId));
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? [...new Set(parsed.filter((id): id is string => typeof id === 'string' && id.length > 0))]
        : [];
    } catch {
      return [];
    }
  },
  setSelectedCalendarIds: (userId: number, calendarIds: string[]) => {
    const uniqueIds = [...new Set(calendarIds.filter((id) => id.length > 0))];
    return enqueueWrite(
      selectedCalendarIdsKey(userId),
      () => AsyncStorage.setItem(selectedCalendarIdsKey(userId), JSON.stringify(uniqueIds))
    );
  },
  /** 9.32 / D-655 — varsayılan link görünümü; seçilmemişse Kısa (Batuhan'ın kararı). */
  getLinkViewDefault: async (userId: number): Promise<'compact' | 'plain' | 'card'> => {
    const value = await AsyncStorage.getItem(linkViewDefaultKey(userId));
    return value === 'plain' || value === 'card' ? value : 'compact';
  },
  setLinkViewDefault: (userId: number, view: 'compact' | 'plain' | 'card') =>
    enqueueWrite(linkViewDefaultKey(userId), () => AsyncStorage.setItem(linkViewDefaultKey(userId), view)),
  /** Yazarken diğer seçenekler gösterilsin mi; seçilmemişse evet. */
  getLinkViewAsk: async (userId: number): Promise<boolean> =>
    (await AsyncStorage.getItem(linkViewAskKey(userId))) !== '0',
  setLinkViewAsk: (userId: number, ask: boolean) =>
    enqueueWrite(linkViewAskKey(userId), () => AsyncStorage.setItem(linkViewAskKey(userId), ask ? '1' : '0')),
  /** D-666 — kişilerin son hesaplanan konuşma duygusu; bilinmeyen ya da yoksa null. */
  getAvatarMoods: async (userId: number, otherIds: number[]): Promise<Record<number, string | null>> => {
    const unique = [...new Set(otherIds)];
    if (unique.length === 0) return {};
    const pairs = await AsyncStorage.multiGet(unique.map((id) => avatarMoodKey(userId, id)));
    const out: Record<number, string | null> = {};
    unique.forEach((id, i) => { out[id] = pairs[i]?.[1] || null; });
    return out;
  },
  setAvatarMood: (userId: number, otherId: number, mood: string | null) =>
    enqueueWrite(avatarMoodKey(userId, otherId), () => mood
      ? AsyncStorage.setItem(avatarMoodKey(userId, otherId), mood)
      : AsyncStorage.removeItem(avatarMoodKey(userId, otherId))),
  getCalendarViewMode: async (userId: number): Promise<'requests' | 'grid' | 'history' | null> => {
    const value = await AsyncStorage.getItem(calendarViewModeKey(userId));
    return value === 'requests' || value === 'grid' || value === 'history' ? value : null;
  },
  setCalendarViewMode: (userId: number, mode: 'requests' | 'grid' | 'history') =>
    enqueueWrite(
      calendarViewModeKey(userId),
      () => AsyncStorage.setItem(calendarViewModeKey(userId), mode)
    ),
  getHiddenExternalCalendarEventKeys: async (userId: number): Promise<string[]> => {
    const raw = await AsyncStorage.getItem(hiddenExternalCalendarEventsKey(userId));
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? [...new Set(parsed.filter((key): key is string => typeof key === 'string' && key.length > 0))]
        : [];
    } catch {
      return [];
    }
  },
  hideExternalCalendarEvent: (userId: number, eventKey: string) => {
    const storageKey = hiddenExternalCalendarEventsKey(userId);
    return enqueueWrite(storageKey, async () => {
      const current = await storage.getHiddenExternalCalendarEventKeys(userId);
      const next = [...new Set([...current, eventKey])].slice(-500);
      await AsyncStorage.setItem(storageKey, JSON.stringify(next));
    });
  },
  clearHiddenExternalCalendarEvents: (userId: number) => enqueueWrite(
    hiddenExternalCalendarEventsKey(userId),
    () => AsyncStorage.removeItem(hiddenExternalCalendarEventsKey(userId))
  ),
  getSystemCalendarEvent: async (
    userId: number,
    meetupId: number
  ): Promise<SystemCalendarEventMapping | null> => {
    const raw = await AsyncStorage.getItem(systemCalendarEventKey(userId, meetupId));
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as Partial<SystemCalendarEventMapping>;
      if (typeof parsed.eventId !== 'string' || typeof parsed.calendarId !== 'string') return null;
      const externallyChanged = parsed.state === 'externally-changed'
        && typeof parsed.observedStartDate === 'string'
        && typeof parsed.observedEndDate === 'string';
      return {
        eventId: parsed.eventId,
        calendarId: parsed.calendarId,
        state: parsed.state === 'externally-removed'
          ? 'externally-removed'
          : externallyChanged ? 'externally-changed' : 'linked',
        ...(externallyChanged ? {
          observedStartDate: parsed.observedStartDate,
          observedEndDate: parsed.observedEndDate,
        } : {}),
        ...(Number.isInteger(parsed.scheduleRevision) && (parsed.scheduleRevision ?? 0) >= 0
          ? { scheduleRevision: parsed.scheduleRevision }
          : {}),
      };
    } catch {
      return null;
    }
  },
  setSystemCalendarEvent: (
    userId: number,
    meetupId: number,
    mapping: SystemCalendarEventMapping
  ) => enqueueWrite(
    systemCalendarEventKey(userId, meetupId),
    () => AsyncStorage.setItem(systemCalendarEventKey(userId, meetupId), JSON.stringify(mapping))
  ),
  clearSystemCalendarEvent: (userId: number, meetupId: number) => enqueueWrite(
    systemCalendarEventKey(userId, meetupId),
    () => AsyncStorage.removeItem(systemCalendarEventKey(userId, meetupId))
  ),
  clearSystemCalendarEventsForUser: (userId: number) => enqueueWrite(
    `system_calendar_events_clear:${userId}`,
    async () => {
      const prefix = `system_calendar_event:${userId}:`;
      const keys = (await AsyncStorage.getAllKeys()).filter((key) => key.startsWith(prefix));
      if (keys.length > 0) await AsyncStorage.multiRemove(keys);
    }
  ),
};

export function decodeToken(token: string): { userId: number } | null {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch {
    return null;
  }
}
